import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { Receipt, ChevronLeft, ShoppingBag, Clock, CheckCircle2, Truck, XCircle, RotateCcw } from "lucide-react";

const STATUS_META = {
  placed:     { ar: "قيد المراجعة", en: "Pending",       color: "#E08A2B", Icon: Clock },
  accepted:   { ar: "قُبل الطلب",   en: "Accepted",      color: "#0086C9", Icon: CheckCircle2 },
  preparing:  { ar: "قيد التحضير",  en: "Preparing",     color: "#0086C9", Icon: Clock },
  ready:      { ar: "جاهز للتوصيل", en: "Ready",         color: "#7C3AED", Icon: ShoppingBag },
  on_the_way: { ar: "في الطريق",    en: "On the way",    color: "#E05D3A", Icon: Truck },
  delivered:  { ar: "تم التوصيل",   en: "Delivered",     color: "#00A85A", Icon: CheckCircle2 },
  cancelled:  { ar: "ملغى",        en: "Cancelled",     color: "#DC2626", Icon: XCircle },
  rejected:   { ar: "مرفوض",       en: "Rejected",      color: "#DC2626", Icon: XCircle },
};

function formatDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const opts = { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" };
    return d.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", opts);
  } catch {
    return iso;
  }
}

export default function Orders() {
  const { user } = useAuth();
  const { lang } = useLang();
  const { replaceCart } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(null);

  const handleReorder = async (order) => {
    setReordering(order.order_id);
    try {
      const { data: rest } = await api.get(`/restaurants/${order.restaurant_id}`);
      const menuById = {};
      (rest.menu || []).forEach((m) => { menuById[m.id] = m; });
      const items = (order.items || [])
        .map((it) => {
          const m = menuById[it.menu_item_id];
          return m ? { ...m, quantity: it.quantity || 1 } : null;
        })
        .filter(Boolean);
      if (items.length === 0) {
        toast.error(lang === "ar" ? "المنتجات لم تعد متوفرة" : "These items are no longer available");
        return;
      }
      replaceCart(rest, items);
      toast.success(lang === "ar" ? "تمت إضافة الطلب إلى السلة" : "Order added to cart");
      navigate("/cart");
    } catch {
      toast.error(lang === "ar" ? "تعذّر إعادة الطلب" : "Could not re-order");
    } finally {
      setReordering(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/orders");
        if (!cancelled) setOrders(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setOrders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (user) load();
    else setLoading(false);
    return () => { cancelled = true; };
  }, [user]);

  // Not logged in
  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center min-h-[60vh] flex flex-col items-center justify-center">
        <div className="w-20 h-20 rounded-3xl bg-[#FEF0EE] flex items-center justify-center mb-5">
          <Receipt className="w-9 h-9 text-[#E05D3A]" strokeWidth={2.2} />
        </div>
        <h1 className="font-display text-2xl font-black text-[#1A1A1A] mb-2">
          {lang === "ar" ? "طلباتي" : "My Orders"}
        </h1>
        <p className="text-[#6B6B6B] text-sm mb-6">
          {lang === "ar" ? "سجّل دخولك لعرض طلباتك" : "Sign in to view your orders"}
        </p>
        <Link
          to="/login"
          className="rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white px-8 py-3 font-bold"
          data-testid="orders-login-cta"
        >
          {lang === "ar" ? "تسجيل الدخول" : "Sign in"}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-5 pt-6 pb-28 bg-[#FAFAFA] min-h-screen">
      <h1 className="font-display text-3xl font-black text-[#1A1A1A] mb-6 text-center">
        {lang === "ar" ? "طلباتي" : "My Orders"}
      </h1>

      {loading ? (
        <div className="space-y-3" data-testid="orders-loading">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-2xl p-4 border border-[#F2F2F2] animate-pulse">
              <div className="h-4 bg-[#EEE] rounded w-1/2 mb-3" />
              <div className="h-3 bg-[#F2F2F2] rounded w-3/4 mb-2" />
              <div className="h-3 bg-[#F2F2F2] rounded w-1/3" />
            </div>
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16" data-testid="orders-empty">
          <div className="inline-flex w-24 h-24 rounded-full bg-[#FEF0EE] items-center justify-center mb-5">
            <ShoppingBag className="w-11 h-11 text-[#E05D3A]" strokeWidth={1.8} />
          </div>
          <h2 className="font-display text-xl font-black text-[#1A1A1A] mb-2">
            {lang === "ar" ? "لا توجد طلبات بعد" : "No orders yet"}
          </h2>
          <p className="text-[#6B6B6B] text-sm mb-6 max-w-xs mx-auto">
            {lang === "ar"
              ? "ابدأ بتصفّح المتاجر واطلب أول طلب لك من جايك"
              : "Start browsing stores and place your first Jayek order"}
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white px-8 py-3 font-bold"
            data-testid="orders-browse-cta"
          >
            {lang === "ar" ? "تصفّح المتاجر" : "Browse stores"}
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="orders-list">
          {orders.map((order) => {
            const meta = STATUS_META[order.status] || STATUS_META.placed;
            const StatusIcon = meta.Icon;
            const restaurantName = lang === "ar" ? order.restaurant_name_ar : order.restaurant_name_en;
            const itemsCount = (order.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
            const firstItems = (order.items || [])
              .slice(0, 2)
              .map((i) => (lang === "ar" ? i.name_ar : i.name_en))
              .join("، ");
            const isActive = !["delivered", "cancelled", "rejected"].includes(order.status);

            return (
              <div
                key={order.order_id}
                className="bg-white rounded-2xl border border-[#F2F2F2] overflow-hidden"
                data-testid={`order-row-${order.order_id}`}
              >
                <button
                  type="button"
                  onClick={() => navigate(`/order/${order.order_id}`)}
                  className="w-full text-start p-4 active:bg-[#FAFAFA] transition"
                  data-testid={`order-open-${order.order_id}`}
                >
                <div className="flex items-start gap-3">
                  <div
                    className="w-11 h-11 rounded-full flex-shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: `${meta.color}1A` }}
                  >
                    <StatusIcon className="w-5 h-5" style={{ color: meta.color }} strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h3 className="font-bold text-[15px] text-[#1A1A1A] truncate">{restaurantName}</h3>
                      <span
                        className="text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                        style={{ backgroundColor: `${meta.color}1A`, color: meta.color }}
                      >
                        {lang === "ar" ? meta.ar : meta.en}
                      </span>
                    </div>
                    <p className="text-xs text-[#6B6B6B] truncate mb-1.5">
                      {firstItems}
                      {(order.items || []).length > 2 ? (lang === "ar" ? " وغيرها" : " and more") : ""}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-[#8B8B8B]">
                        {formatDate(order.created_at, lang)} ·{" "}
                        {lang === "ar" ? `${itemsCount} عنصر` : `${itemsCount} item${itemsCount > 1 ? "s" : ""}`}
                      </span>
                      <span className="font-bold text-[13px] text-[#1A1A1A]">
                        {Number(order.total || 0).toFixed(2)} {lang === "ar" ? "﷼" : "SAR"}
                      </span>
                    </div>
                    {isActive && (
                      <div
                        className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold"
                        style={{ color: meta.color }}
                      >
                        {lang === "ar" ? "اضغط للمتابعة" : "Tap to track"}
                        <ChevronLeft className={`w-3.5 h-3.5 ${lang === "ar" ? "" : "rotate-180"}`} />
                      </div>
                    )}
                  </div>
                </div>
                </button>
                <div className="px-4 pb-3 -mt-1">
                  <button
                    type="button"
                    onClick={() => handleReorder(order)}
                    disabled={reordering === order.order_id}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#E05D3A] text-[#E05D3A] py-2.5 text-[13.5px] font-bold active:bg-[#FEF0EE] disabled:opacity-60 transition"
                    data-testid={`order-reorder-${order.order_id}`}
                  >
                    <RotateCcw className={`w-4 h-4 ${reordering === order.order_id ? "animate-spin" : ""}`} />
                    {reordering === order.order_id
                      ? (lang === "ar" ? "جارٍ الإضافة..." : "Adding...")
                      : (lang === "ar" ? "إعادة الطلب" : "Re-order")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
